import { Fragment, FunctionComponent, useState, useCallback } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    useGetVoucherTemplatesQuery,
    useDeleteVoucherTemplateMutation,
    usePatchVoucherTemplateActiveMutation,
    usePostVoucherTemplateMutation,
    useLazyGetVoucherTemplateQuery,
    VoucherTemplateAdminDTO,
} from '@starjack/entities/voucherTemplate';
import { useNotification } from '@starjack/features/notifications';
import {
    PencilSquareIcon,
    TrashIcon,
    DocumentDuplicateIcon,
    PlusIcon,
} from '@heroicons/react/24/solid';
import { SvgIcon } from '@mui/material';
import {
    StajackTableColumn,
    StarjackTable,
    usePagination,
} from '@starjack/shared-components';

type VoucherTemplateListContentProps = {
    liftOperatorId?: string;
};

export const VoucherTemplateListContent: FunctionComponent<
    VoucherTemplateListContentProps
> = ({ liftOperatorId }) => {
    const navigate = useNavigate();
    const { addNotification } = useNotification();

    const { data: templates = [], isLoading } = useGetVoucherTemplatesQuery(
        liftOperatorId ? { liftOperatorId } : undefined
    );
    const [deleteTemplate] = useDeleteVoucherTemplateMutation();
    const [patchActive] = usePatchVoucherTemplateActiveMutation();
    const [postTemplate] = usePostVoucherTemplateMutation();
    const [getTemplateDetail] = useLazyGetVoucherTemplateQuery();

    const { currentItems, page, setNextPage, itemsPerPage, setItemsPerPage } =
        usePagination({ items: templates, resetKey: liftOperatorId ?? '' });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] =
        useState<VoucherTemplateAdminDTO | null>(null);

    const handleEdit = useCallback(
        (id: string) => {
            navigate(`/gutscheinvorlagen/bearbeiten?id=${id}`);
        },
        [navigate]
    );

    const handleCreate = useCallback(() => {
        navigate('/gutscheinvorlagen/bearbeiten');
    }, [navigate]);

    const handleToggleActive = useCallback(
        async (template: VoucherTemplateAdminDTO) => {
            try {
                await patchActive({
                    id: template.id,
                    body: { active: !template.active },
                }).unwrap();
                addNotification({
                    type: 'success',
                    message: template.active
                        ? `Vorlage "${template.name}" deaktiviert.`
                        : `Vorlage "${template.name}" aktiviert.`,
                });
            } catch (err) {
                // Surface the backend reason so the user knows why activation was
                // rejected (e.g. not all fields placed yet) instead of a generic error.
                const code = (
                    err as { data?: { errors?: { error?: string }[] } }
                )?.data?.errors?.[0]?.error;
                addNotification({
                    type: 'error',
                    message:
                        code === 'template_incomplete'
                            ? 'Vorlage unvollständig: Bitte alle Felder (Text, Betrag, Code, Absender, Empfänger) platzieren, bevor die Vorlage aktiviert wird.'
                            : 'Fehler beim Ändern des Status.',
                });
            }
        },
        [patchActive, addNotification]
    );

    const handleDeleteConfirm = useCallback(async () => {
        if (!templateToDelete) return;
        try {
            await deleteTemplate(templateToDelete.id).unwrap();
            addNotification({
                type: 'success',
                message: `Vorlage "${templateToDelete.name}" gelöscht.`,
            });
        } catch {
            addNotification({
                type: 'error',
                message:
                    'Vorlage kann nicht gelöscht werden, da sie von bestehenden Gutscheinen verwendet wird.',
            });
        }
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
    }, [templateToDelete, deleteTemplate, addNotification]);

    const handleDuplicate = useCallback(
        async (template: VoucherTemplateAdminDTO) => {
            try {
                const detail = await getTemplateDetail(template.id).unwrap();

                const result = await postTemplate({
                    liftOperatorId: template.liftOperatorId,
                    language: template.language,
                    name: `${template.name} (Kopie)`,
                    defaultText: template.defaultText,
                    elements: detail.elements,
                }).unwrap();
                addNotification({
                    type: 'success',
                    message: `Vorlage "${template.name}" dupliziert. Bitte das Hintergrund-PDF erneut hochladen.`,
                });
                navigate(`/gutscheinvorlagen/bearbeiten?id=${result.id}`);
            } catch {
                addNotification({
                    type: 'error',
                    message: 'Fehler beim Duplizieren.',
                });
            }
        },
        [getTemplateDetail, postTemplate, addNotification, navigate]
    );

    const columns: StajackTableColumn<VoucherTemplateAdminDTO>[] = [
        {
            label: 'Name',
            itemKey: 'name',
        },
        {
            label: 'Sprache',
            itemKey: 'language',
        },
        {
            label: 'Status',
            itemTransform: (template) => (
                <Chip
                    label={template.active ? 'Aktiv' : 'Entwurf'}
                    color={template.active ? 'success' : 'default'}
                    size='small'
                    onClick={() => handleToggleActive(template)}
                    sx={{ cursor: 'pointer' }}
                />
            ),
        },
        {
            label: 'Quelle',
            itemKey: 'source',
        },
        {
            label: 'Aktionen',
            itemTransform: (template) => (
                <Stack direction='row' spacing={0.5}>
                    <Tooltip title='Bearbeiten'>
                        <IconButton
                            size='small'
                            onClick={() => handleEdit(template.id)}
                        >
                            <SvgIcon fontSize='small'>
                                <PencilSquareIcon />
                            </SvgIcon>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Duplizieren'>
                        <IconButton
                            size='small'
                            onClick={() => handleDuplicate(template)}
                        >
                            <SvgIcon fontSize='small'>
                                <DocumentDuplicateIcon />
                            </SvgIcon>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Löschen'>
                        <IconButton
                            size='small'
                            onClick={() => {
                                setTemplateToDelete(template);
                                setDeleteDialogOpen(true);
                            }}
                        >
                            <SvgIcon fontSize='small'>
                                <TrashIcon />
                            </SvgIcon>
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
        },
    ];

    return (
        <Fragment>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                }}
            >
                <Typography variant='h4'>Gutscheinvorlagen</Typography>
                <Button
                    variant='contained'
                    startIcon={
                        <SvgIcon fontSize='small'>
                            <PlusIcon />
                        </SvgIcon>
                    }
                    onClick={handleCreate}
                >
                    Neue Vorlage
                </Button>
            </Box>

            <StarjackTable<VoucherTemplateAdminDTO>
                count={templates.length}
                items={currentItems}
                columns={columns}
                itemId='id'
                currentPage={page}
                onPageChange={setNextPage}
                rowsPerPage={itemsPerPage}
                onRowsPerPageChange={setItemsPerPage}
                loading={isLoading}
                noDataLabel='Noch keine Gutscheinvorlagen vorhanden.'
            />

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Vorlage löschen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Vorlage &quot;{templateToDelete?.name}&quot; wirklich
                        löschen? Diese Aktion kann nicht rückgängig gemacht
                        werden.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color='error'
                        variant='contained'
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};
